"use client"

import type React from "react"
import { useState } from "react"
import { Col, Container, Row, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap"
import { FaArrowLeft, FaBuilding } from "react-icons/fa"
import Breadcrumbs from '../../../CommonElements/Breadcrumbs/Breadcrumbs';
import ExhibitorSelection from "./exhibitor-selection"
import ParticipationsList from "../BadgeRequest/ParticipationsList"
import RepresentationsList from "../BadgeRequest/RepresentationsList"
import UserOrdersList from "../BadgeRequest/UserOrdersList"
import BadgeReprintList from "../BadgeRequest/BadgeReprintList"
import StaffManagement from "./staff-management"
import CarManagement from "./car-management"
import PaymentModal from "../BadgeRequest/PaymentModal"
import UserOrderDetailsModal from "../BadgeRequest/UserOrderDetailsModal"
import type { ExhibitorVerification, Participation, PrintOrder } from "../BadgeRequest/badge-request"
import type { Exhibitor, VerifiedExhibitor } from "./types"
import { toast } from "react-toastify"

const CompanyManagementContainer: React.FC = () => {
  const [activeTab, setActiveTab] = useState("1")
  const [selectedCompany, setSelectedCompany] = useState<ExhibitorVerification | Exhibitor | null>(null)
  const [selectedParticipation, setSelectedParticipation] = useState<Participation | null>(null)
  const [currentStep, setCurrentStep] = useState<"companies" | "participations" | "management">("companies")

  // Modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<PrintOrder | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  const handleCompanySelect = (company: ExhibitorVerification | Exhibitor | VerifiedExhibitor) => {
    const normalizedCompany: ExhibitorVerification = {
      id: company.id,
      user: "user" in company ? company.user : 0,
      exhibitor: "exhibitor" in company ? company.exhibitor : company.id,
      exhibitor_name: "exhibitor_name" in company ? company.exhibitor_name : company.name,
      exhibitor_email: "exhibitor_email" in company ? company.exhibitor_email : company.email,
      status: ("status" in company ? company.status : "verified") as "pending" | "verified" | "expired" | "failed",
      created_at: "created_at" in company ? company.created_at : new Date().toISOString(),
      verified_at: "verified_at" in company ? company.verified_at : new Date().toISOString(),
      is_active: "is_active" in company ? company.is_active : true,
    }

    setSelectedCompany(normalizedCompany)
    setCurrentStep("participations")
  }

  const handleParticipationSelect = (participation: Participation) => {
    setSelectedParticipation(participation)
    setCurrentStep("management")
  }

  const handleBackToCompanies = () => {
    setSelectedCompany(null)
    setSelectedParticipation(null)
    setCurrentStep("companies")
    setActiveTab("1") // Reset to first tab
  }

  const handleBackToParticipations = () => {
    setSelectedParticipation(null)
    setCurrentStep("participations")
    setActiveTab("1") // Reset to first tab
  }

  const handleOrderCreated = (order: PrintOrder) => {
    try {
      const totalAmount =
        order.order_details?.reduce((total, detail) => {
          return total + Number.parseFloat(detail.amount)
        }, 0) || 0

      setRefreshTrigger((prev) => !prev)

      let message = `Order ${order.order_number} created successfully!`
      if (order.is_postpaid) {
        message += " Payment will be processed later for postpaid account."
      } else if (totalAmount > 0) {
        message += ` Payment of ${totalAmount.toLocaleString()} RWF is required.`
      } else {
        message += " All badges are free."
      }

      toast.success(message)

      // Switch to orders tab
      setTimeout(() => {
        setActiveTab("4") // Switch to My Orders tab
      }, 2000)
    } catch (error) {
      console.error("Error in handleOrderCreated:", error)
      toast.error("There was an issue processing your order")
    }
  }

  const handleViewOrder = (order: PrintOrder) => {
    setSelectedOrder(order)
    setOrderDetailsModalOpen(true)
  }

  const handlePayOrder = (order: PrintOrder) => {
    setSelectedOrder(order)
    setPaymentModalOpen(true)
  }

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false)
    setSelectedOrder(null)
    setRefreshTrigger((prev) => !prev)
    toast.success("Payment submitted successfully!")
  }

  const handleCloseOrderDetails = () => {
    setOrderDetailsModalOpen(false)
    setSelectedOrder(null)
  }

  if (currentStep === "companies") {
    return (
      <>
        <Breadcrumbs mainTitle="Company Management" parent="Exhibitor Access" />
        <Container fluid>
          <Row>
            <Col md="12">
              <ExhibitorSelection
                onExhibitorSelected={handleCompanySelect}
                onVerifiedExhibitorSelected={handleCompanySelect}
              />
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  if (currentStep === "participations" && selectedCompany) {
    return (
      <>
        <Breadcrumbs mainTitle="Select Participation" parent="Company Management" />
        <Container fluid>
          <Row>
            <Col md="12">
              <ParticipationsList
                exhibitor={selectedCompany as ExhibitorVerification}
                onParticipationSelect={handleParticipationSelect}
                onBack={handleBackToCompanies}
              />
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  return (
    <>
      <Breadcrumbs mainTitle="Company Management" parent="Exhibitor Access" />
      <Container fluid>
        <Row>
          <Col md="12">
            {/* Company Header */}
            <div className="company-header mb-4 p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Button color="secondary" size="sm" onClick={handleBackToParticipations} className="me-3">
                    <FaArrowLeft size={12} />
                  </Button>
                  <div className="d-flex align-items-center">
                    <FaBuilding className="text-primary me-2" size={20} />
                    <div>
                      <h5 className="mb-0">{(selectedCompany as ExhibitorVerification)?.exhibitor_name}</h5>
                      <small className="text-muted">
                        {selectedParticipation?.exhibition_title} - {selectedParticipation?.stand_category_name}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <small className="text-muted">Max Participants: {selectedParticipation?.num_representatives}</small>
                </div>
              </div>
            </div>

            {/* Enhanced Styled Tabs */}
            <div className="simple-tabs-container mb-4">
              <Nav className="simple-nav-tabs">
                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "1" ? "active" : ""}`}
                    onClick={() => setActiveTab("1")}
                  >
                    Staff Management
                  </NavLink>
                </NavItem>
                 <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "2" ? "active" : ""}`}
                    onClick={() => setActiveTab("2")}
                  >
                    Car Management
                  </NavLink>
                </NavItem>

                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "3" ? "active" : ""}`}
                    onClick={() => setActiveTab("3")}
                  >
                    Request New Badges
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "4" ? "active" : ""}`}
                    onClick={() => setActiveTab("4")}
                  >
                    Request Badge Reprints
                  </NavLink>
                </NavItem>
                

                <NavItem>
                  <NavLink
                    className={`simple-nav-link ${activeTab === "5" ? "active" : ""}`}
                    onClick={() => setActiveTab("5")}
                  >
                  Orders
                  </NavLink>
                </NavItem>
               
              </Nav>
            </div>

            <TabContent activeTab={activeTab}>

               {/* Staff Management Tab */}
              <TabPane tabId="1">
                <div className="pt-3">
                  {selectedParticipation && (
                    <StaffManagement exhibitor={selectedCompany} participation={selectedParticipation} />
                  )}
                </div>
              </TabPane>

              {/* Car Management Tab */}
              <TabPane tabId="2">
                <div className="pt-3">
                  {selectedParticipation && (
                    <CarManagement
                      participationId={selectedParticipation.id}
                      exhibitorName={(selectedCompany as ExhibitorVerification)?.exhibitor_name || ""}
                    />
                  )}
                </div>
              </TabPane>


              {/* New Badge Requests Tab */}
              <TabPane tabId="3">
                <div className="pt-3">
                  {selectedParticipation && (
                    <RepresentationsList
                      participation={selectedParticipation}
                      onBack={handleBackToParticipations}
                      onOrderCreated={handleOrderCreated}
                    />
                  )}
                </div>
              </TabPane>

              {/* Badge Reprint Requests Tab */}
              <TabPane tabId="4">
                <div className="pt-3">
                  {selectedParticipation && (
                    <BadgeReprintList participation={selectedParticipation} onOrderCreated={handleOrderCreated} />
                  )}
                </div>
              </TabPane>

             

              {/* Orders Tab */}
              <TabPane tabId="5">
                <div className="pt-3">
                  <UserOrdersList onViewOrder={handleViewOrder} onPayOrder={handlePayOrder} refresh={refreshTrigger} />
                </div>
              </TabPane>

              
            </TabContent>
          </Col>
        </Row>

        {/* Payment Modal */}
        {paymentModalOpen && selectedOrder && (
          <PaymentModal
            isOpen={paymentModalOpen}
            toggle={() => setPaymentModalOpen(false)}
            order={selectedOrder}
            onSuccess={handlePaymentSuccess}
          />
        )}

        {/* User Order Details Modal */}
        {orderDetailsModalOpen && selectedOrder && (
          <UserOrderDetailsModal
            isOpen={orderDetailsModalOpen}
            toggle={handleCloseOrderDetails}
            order={selectedOrder}
          />
        )}
      </Container>

      {/* Enhanced Styles */}
      <style>{`
        .company-header {
          border-left: 4px solid #007bff;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .simple-tabs-container {
          border-bottom: 1px solid #e9ecef;
        }

        .simple-nav-tabs {
          border: none !important;
          background: transparent;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .simple-nav-link {
          border: none !important;
          background: transparent !important;
          padding: 1rem 0.5rem !important;
          margin: 0 !important;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #6c757d !important;
          font-weight: 500;
          position: relative;
          border-radius: 0 !important;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .simple-nav-link:hover {
          color: #007bff !important;
          background: transparent !important;
        }

        .simple-nav-link.active {
          color: #007bff !important;
          background: transparent !important;
        }

        .simple-nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #007bff;
          border-radius: 2px 2px 0 0;
        }

        .simple-nav-link:hover::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #007bff;
          opacity: 0.5;
          border-radius: 1px 1px 0 0;
        }

        .simple-nav-link.active:hover::after {
          opacity: 1;
          height: 3px;
        }

        @media (max-width: 768px) {
          .simple-nav-tabs {
            gap: 0.5rem;
          }
          
          .simple-nav-link {
            font-size: 0.8rem;
            padding: 0.75rem 0.25rem !important;
          }
        }
      `}</style>
    </>
  )
}

export default CompanyManagementContainer
